import { IBulkWalkthroughRequest } from 'src/services/client-types';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { filterUpdatedSelectedSuiteIds } from 'src/components/BulkOperations/helpers';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';

interface UseBulkAddWalkthroughsParams {
  userId: string;
  initialSelectedSuiteIds: string[];
  onSave: (params: IBulkWalkthroughRequest) => void;
  suites: UnitBulkOperationDto[];
  bookingLinkId: string | null;
}

export interface UseBulkAddWalkthroughsReturn {
  // Form state.
  walkthroughName: string;
  setWalkthroughName: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  walkthroughType: string;
  setWalkthroughType: (value: string) => void;
  permissionType: string;
  setPermissionType: (value: string) => void;
  // Checklist.
  checklistItems: string[];
  handleAddChecklistItem: () => void;
  handleRemoveChecklistItem: (index: number) => void;
  handleChecklistItemChange: (index: number, value: string) => void;
  isChecklistItemEmpty: (index: number) => boolean;
  // Suite selection.
  selectedSuiteIds: string[];
  handleSuiteSelectAll: (eligibleSuiteIds: string[]) => void;
  handleSuiteSelectIndividual: (suiteId: string) => void;
  handleDeselectAllSuites: () => void;
  // Activation.
  activateWalkthroughs: boolean;
  setActivateWalkthroughs: (value: boolean) => void;
  sendSchedulingEmail: boolean;
  handleSendSchedulingEmailChange: (value: boolean) => void;
  showConfirmation: boolean
  setShowConfirmation: Dispatch<SetStateAction<boolean>>
  // Actions.
  isFormValid: () => boolean;
  saveBulkWalkthroughs: () => void;
  confirmSave: () => void;
}

export const useBulkAddWalkthroughs = ({
  userId,
  initialSelectedSuiteIds,
  onSave,
  suites,
  bookingLinkId,
}: UseBulkAddWalkthroughsParams): UseBulkAddWalkthroughsReturn => {
  const [walkthroughName, setWalkthroughName] = useState('');
  const [description, setDescription] = useState('');
  const [walkthroughType, setWalkthroughType] = useState('');
  const [permissionType, setPermissionType] = useState('');
  const [checklistItems, setChecklistItems] = useState<string[]>(['']);
  const [selectedSuiteIds, setSelectedSuiteIds] = useState<string[]>(
    initialSelectedSuiteIds
  );
  const [activateWalkthroughs, setActivateWalkthroughs] = useState(false);
  const [sendSchedulingEmail, setSendSchedulingEmail] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);


  useEffect(() => {
    if (initialSelectedSuiteIds.length > 0) {
      setSelectedSuiteIds(initialSelectedSuiteIds);
    }
  }, [initialSelectedSuiteIds]);

  const handleSuiteSelectAll = (eligibleSuiteIds: string[]) => {
    setSelectedSuiteIds(eligibleSuiteIds);
  };

  const handleSuiteSelectIndividual = (suiteId: string) => {
    const updated: string[] = filterUpdatedSelectedSuiteIds(
      suiteId,
      selectedSuiteIds
    );
    setSelectedSuiteIds(updated);
  };

  const handleDeselectAllSuites = () => {
    setSelectedSuiteIds([]);
  };

  const handleAddChecklistItem = () => {
    setChecklistItems([...checklistItems, '']);
  };

  const handleRemoveChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleChecklistItemChange = (index: number, value: string) => {
    const updated: string[] = checklistItems.map((item, i) =>
      i === index ? value : item
    );
    setChecklistItems(updated);
  };

  const isChecklistItemEmpty = (index: number): boolean => {
    return walkthroughType === 'Enhanced' && !checklistItems[index]?.trim();
  };

  const hasEmptyChecklistItems = () => {
    if (walkthroughType === 'Enhanced') {
      return checklistItems.some((item: string) => !item.trim());
    }

    return false;
  };

  const isFormValid = () => {
    const basicValidation =
      walkthroughName.trim() &&
      walkthroughType &&
      permissionType &&
      selectedSuiteIds.length > 0;

    if (walkthroughType === 'Enhanced') {
      return basicValidation && !hasEmptyChecklistItems();
    }

    return basicValidation;
  };

  const deselectSuitesWithoutPrimaryContact = () => {
    const validSelectedSuiteIds: string[] = selectedSuiteIds.filter(
      (suiteId: string) => {
        const suite: UnitBulkOperationDto = suites.find(
          (suite: UnitBulkOperationDto) => {
            return suite.id === suiteId;
          }
        );

        return suite?.hasPrimaryContact;
      }
    );

    setSelectedSuiteIds(validSelectedSuiteIds);
  };

  const handleSendSchedulingEmailChange = (value: boolean) => {
    setSendSchedulingEmail(value);

    if (value) {
      deselectSuitesWithoutPrimaryContact();
    }
  };

  const handlePermissionTypeChange = (value: string) => {
    setPermissionType(value);

    if (value === 'Internal') {
      setSendSchedulingEmail(false);
      setActivateWalkthroughs(false);
    }
  };

  const saveBulkWalkthroughs = () => {
    setShowConfirmation(true);
  };

  const confirmSave = () => {
    const payload: IBulkWalkthroughRequest = {
      walkthrough: {
        title: walkthroughName,
        description,
        walkthroughType,
        walkthroughName,
        permissions: permissionType,
        userId,
        status: walkthroughType === 'Enhanced' ? 'Activate' : 'Configure',
      },
      checklistItems:
        walkthroughType === 'Enhanced'
          ? checklistItems.filter((item) => item.trim())
          : [],
      units: selectedSuiteIds,
      walkthroughPermissions: permissionType,
      activateWalkthroughs:
        permissionType === 'Homeowner or Occupant' ? activateWalkthroughs : undefined,
      sendSchedulingEmail:
        permissionType === 'Homeowner or Occupant' ? sendSchedulingEmail : undefined,
      propertyBookingLinkId: bookingLinkId,
    };

    onSave(payload);
    setShowConfirmation(false);
  };

  return {
    walkthroughName,
    setWalkthroughName,
    description,
    setDescription,
    walkthroughType,
    setWalkthroughType,
    permissionType,
    setPermissionType: handlePermissionTypeChange,
    checklistItems,
    handleAddChecklistItem,
    handleRemoveChecklistItem,
    handleChecklistItemChange,
    isChecklistItemEmpty,
    selectedSuiteIds,
    handleSuiteSelectAll,
    handleSuiteSelectIndividual,
    handleDeselectAllSuites,
    activateWalkthroughs,
    setActivateWalkthroughs,
    sendSchedulingEmail,
    handleSendSchedulingEmailChange,
    isFormValid,
    saveBulkWalkthroughs,
    confirmSave,
    showConfirmation,
    setShowConfirmation
  };
};
